import type { Schemas } from "@shared/api/types";

// Lógica pura del cruce Zoho/Redmine que ve el front: parseo de lo que hay en
// localStorage (con compatibilidad hacia atrás) y el view model del aviso de
// asignaciones sin cruzar. Sin React, sin i18n, sin MUI: testeable sin montar nada.

export type ProjectWithAssignment = Schemas["ProjectWithAssigmentDTO"];
export type UnmatchedAssignment = Schemas["UnmatchedAssignmentDTO"];
export type ProjectsWithAssignmentsResponse = Schemas["ProjectsWithAssignmentsResponseDTO"];

export interface StoredProjectAssignments {
  projects: ProjectWithAssignment[];
  unmatched: UnmatchedAssignment[];
  supportReference: string | null;
}

const EMPTY_STORED: StoredProjectAssignments = {
  projects: [],
  unmatched: [],
  supportReference: null,
};

const isEnvelopeShape = (value: unknown): value is ProjectsWithAssignmentsResponse =>
  typeof value === "object" &&
  value !== null &&
  Array.isArray((value as { projects?: unknown }).projects);

/**
 * Lee lo que `useProjectAssignments` dejó en `localStorage`. Tiene que aceptar tres
 * formas: el envelope actual, el array sin envolver que dejaba la versión anterior del
 * endpoint (un browser puede seguir teniéndolo guardado justo después del deploy) y
 * cualquier otra cosa (null, JSON roto, basura de otra versión), que se trata como
 * "sin datos" en vez de romper el dialog.
 */
export const parseStoredProjectAssignments = (raw: string | null): StoredProjectAssignments => {
  if (!raw) return EMPTY_STORED;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_STORED;
  }

  if (Array.isArray(parsed)) {
    return { projects: parsed as ProjectWithAssignment[], unmatched: [], supportReference: null };
  }

  if (isEnvelopeShape(parsed)) {
    return {
      projects: parsed.projects,
      unmatched: parsed.unmatched ?? [],
      supportReference: parsed.supportReference ?? null,
    };
  }

  return EMPTY_STORED;
};

/** No hay nada para elegir: ni staffings ni proyectos. */
export const hasNoProjectOptions = (staffing: unknown[], projects: unknown[]): boolean =>
  staffing.length === 0 && projects.length === 0;

export interface UnmatchedAssignmentsViewModel {
  /** Claves de Zoho (distintas) que no cruzaron con ninguna membresía de Redmine. */
  keyedKeys: string[];
  /** "<código> / ref <supportReference>", o solo "<código>" si no hay supportReference. */
  keyedReference: string | null;
  /** Hay al menos una asignación activa sin Key_Redmine configurada. */
  hasNullKeyEntry: boolean;
  nullKeyReference: string | null;
}

const hasRedmineProjectKey = (
  entry: UnmatchedAssignment
): entry is UnmatchedAssignment & { redmineProjectKey: string } => entry.redmineProjectKey != null;

const buildReference = (diagnosticCode: string, supportReference: string | null): string =>
  supportReference ? `${diagnosticCode} / ref ${supportReference}` : diagnosticCode;

/**
 * Arma el view model del aviso a partir de `unmatched`. Separa las entradas con clave
 * (ZH-104 / RM-202) de las que no tienen clave (ZH-103): son dos mensajes distintos que
 * pueden aparecer juntos. El código de cada línea es el de sus propias entradas — dentro
 * de una misma respuesta todas comparten el mismo código, porque el backend lo calcula
 * una sola vez por grupo.
 */
export const buildUnmatchedAssignmentsViewModel = (
  unmatched: UnmatchedAssignment[],
  supportReference: string | null
): UnmatchedAssignmentsViewModel => {
  const keyedEntries = unmatched.filter(hasRedmineProjectKey);
  const nullKeyEntries = unmatched.filter((entry) => entry.redmineProjectKey == null);

  const keyedKeys = Array.from(new Set(keyedEntries.map((entry) => entry.redmineProjectKey)));

  return {
    keyedKeys,
    // En ambas referencias el ternario ya verifico que hay al menos una entrada.
    keyedReference:
      keyedEntries.length > 0 ? buildReference(keyedEntries[0]!.diagnosticCode, supportReference) : null,
    hasNullKeyEntry: nullKeyEntries.length > 0,
    nullKeyReference:
      nullKeyEntries.length > 0 ? buildReference(nullKeyEntries[0]!.diagnosticCode, supportReference) : null,
  };
};
